import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Segment,
  Header,
  Message,
  Loader,
  Dimmer,
  Checkbox,
  Confirm,
  List,
  Label,
} from "semantic-ui-react";
import axios from "../../../apis/dvdlocator";

interface AsinListing {
  listing_id: string;
  msku: string | null;
  listing_title: string | null;
}

interface ProductAsin {
  asin: string;
  listings: AsinListing[];
}

interface RemoveAsinsFromProductProps {
  productId: number;
  productTitle: string;
  onClose: (didRemove?: boolean) => void;
}

export function RemoveAsinsFromProduct({
  productId,
  productTitle,
  onClose,
}: RemoveAsinsFromProductProps) {
  const [asins, setAsins] = useState<ProductAsin[]>([]);
  const [loadingAsins, setLoadingAsins] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedAsins, setSelectedAsins] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeProgress, setRemoveProgress] = useState({ completed: 0, total: 0 });
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [totalRemoved, setTotalRemoved] = useState(0);

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchAsins() {
      setLoadingAsins(true);
      setLoadError(null);
      try {
        const res = await axios.get(`/product/${productId}/listings`, {
          signal: abortController.signal,
        });
        setAsins(res.data.asins ?? []);
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          setLoadError(
            err.response?.data?.message ||
              "Failed to load ASINs. Please close and try again."
          );
        }
      } finally {
        if (!abortController.signal.aborted) setLoadingAsins(false);
      }
    }

    fetchAsins();
    return () => abortController.abort();
  }, [productId]);

  const allSelected = asins.length > 0 && selectedAsins.size === asins.length;
  const someSelected = selectedAsins.size > 0 && !allSelected;

  const toggleAsin = (asin: string) => {
    setSelectedAsins((prev) => {
      const next = new Set(prev);
      if (next.has(asin)) {
        next.delete(asin);
      } else {
        next.add(asin);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedAsins(allSelected ? new Set() : new Set(asins.map((a) => a.asin)));
  };

  const handleRemoveSelected = async () => {
    setConfirmOpen(false);
    setIsRemoving(true);
    setRemoveError(null);

    const asinList = Array.from(selectedAsins);
    const failedAsins: string[] = [];

    setRemoveProgress({ completed: 0, total: asinList.length });

    for (let i = 0; i < asinList.length; i++) {
      const asin = asinList[i];
      try {
        await axios.delete(`/product/${productId}/listing/${asin}`);
      } catch (err: any) {
        failedAsins.push(
          `${asin}: ${err.response?.data?.message || "An error occurred."}`
        );
      }
      setRemoveProgress({ completed: i + 1, total: asinList.length });
    }

    const removedCount = asinList.length - failedAsins.length;

    setIsRemoving(false);
    setTotalRemoved((prev) => prev + removedCount);
    setSelectedAsins(new Set());
    setRemoveError(failedAsins.length > 0 ? failedAsins.join("\n") : null);

    if (removedCount > 0) {
      const removedSet = new Set(
        asinList.filter((asin) => !failedAsins.some((f) => f.startsWith(`${asin}:`)))
      );
      setAsins((prev) => prev.filter((a) => !removedSet.has(a.asin)));
    }
  };

  return (
    <Modal open onClose={() => onClose(totalRemoved > 0)} closeOnDimmerClick={false} size="large">
      <Modal.Header>Remove ASINs from Product</Modal.Header>
      <Modal.Content>
        <Header as="h4" dividing>
          Selected Product
        </Header>
        <Table definition compact>
          <Table.Body>
            <Table.Row>
              <Table.Cell width={4}>Product ID</Table.Cell>
              <Table.Cell>{productId}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Product Title</Table.Cell>
              <Table.Cell>{productTitle}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
          Linked ASINs
        </Header>
        <p style={{ color: "#666", fontSize: "0.9em" }}>
          Select one, many, or all ASINs to unlink from this product. The
          underlying listings are not deleted, only the link to this product.
        </p>

        {loadError && (
          <Message negative>
            <p>{loadError}</p>
          </Message>
        )}

        {removeError && (
          <Message negative>
            <Message.Header>Some ASINs could not be removed</Message.Header>
            <p style={{ whiteSpace: "pre-line" }}>{removeError}</p>
          </Message>
        )}

        {totalRemoved > 0 && !removeError && (
          <Message positive>
            <p>
              Removed {totalRemoved} ASIN{totalRemoved > 1 ? "s" : ""} from this
              product.
            </p>
          </Message>
        )}

        {loadingAsins ? (
          <Segment style={{ minHeight: "60px" }}>
            <Dimmer active inverted>
              <Loader size="small">Loading ASINs…</Loader>
            </Dimmer>
          </Segment>
        ) : asins.length === 0 ? (
          <Segment textAlign="center" style={{ color: "#999" }}>
            No ASINs linked to this product
          </Segment>
        ) : (
          <>
            <Checkbox
              label="Select All"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleSelectAll}
              disabled={isRemoving}
              style={{ marginBottom: "0.75em" }}
            />
            <List divided relaxed celled>
              {asins.map((asinGroup) => (
                <List.Item key={asinGroup.asin}>
                  <div style={{ display: "flex", alignItems: "flex-start" }}>
                    <Checkbox
                      checked={selectedAsins.has(asinGroup.asin)}
                      onChange={() => toggleAsin(asinGroup.asin)}
                      disabled={isRemoving}
                      style={{ marginRight: "0.75em", marginTop: "0.2em" }}
                    />
                    <List.Content style={{ flex: 1 }}>
                      <List.Header>
                        <Label color="blue" horizontal>
                          ASIN
                        </Label>
                        {asinGroup.asin}
                      </List.Header>
                      {asinGroup.listings.length > 0 ? (
                        <List.List style={{ marginTop: "0.5em" }}>
                          {asinGroup.listings.map((listing) => (
                            <List.Item key={listing.listing_id}>
                              <List.Content>
                                <List.Description>
                                  <Label basic horizontal size="tiny">
                                    MSKU
                                  </Label>
                                  {listing.msku ?? "—"}
                                  <span style={{ marginLeft: "1em", color: "#666" }}>
                                    {listing.listing_title ?? "—"}
                                  </span>
                                </List.Description>
                              </List.Content>
                            </List.Item>
                          ))}
                        </List.List>
                      ) : (
                        <List.Description style={{ color: "#999", marginTop: "0.5em" }}>
                          No associated listings
                        </List.Description>
                      )}
                    </List.Content>
                  </div>
                </List.Item>
              ))}
            </List>
          </>
        )}

        {isRemoving && (
          <Segment style={{ minHeight: "60px" }}>
            <Dimmer active inverted>
              <Loader size="small">
                Removing… {removeProgress.completed}/{removeProgress.total}
              </Loader>
            </Dimmer>
          </Segment>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose(totalRemoved > 0)} disabled={isRemoving}>
          Close
        </Button>
        <Button
          negative
          onClick={() => setConfirmOpen(true)}
          disabled={selectedAsins.size === 0 || isRemoving || loadingAsins}
          loading={isRemoving}
        >
          Remove Selected ({selectedAsins.size})
        </Button>
      </Modal.Actions>

      <Confirm
        open={confirmOpen}
        content={`This will unlink ${selectedAsins.size} ASIN${
          selectedAsins.size > 1 ? "s" : ""
        } from this product. This cannot be undone.`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleRemoveSelected}
      />
    </Modal>
  );
}
